/**
 * The job grid's columns, shared by the L1 "Failures now" panel and the L2 job
 * list so the two can never render the same job differently.
 *
 * Every job name is a door into the L2 drawer (`?job=<source>:<name>`), which
 * is where the run history and the acknowledgement form live.
 *
 * Tone comes from the backend's normalised `status`, never from `last_result`
 * alone — `last_result` is the raw source vocabulary and says `None` for both
 * "never run" and "no result yet", which are not the same thing.
 */

import { Icon } from "@norican/icons";
import { Badge, type DataGridColumnDef } from "@norican/react";

import type { BackupJobRow } from "@/lib/api";
import { formatCount, formatRelativeTime } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { BACKUP_JOBS_PATH, JOB_DRAWER_PARAM, jobKey } from "./params";
import { formatDuration, formatTimestamp, sourceLabel, statusStyle } from "./status";

/** Status badge — icon plus label, so the outcome is never colour-alone (§3.2). */
export function StatusBadge({ status, size = "xs" }: { status: string; size?: "xs" | "sm" }) {
  const style = statusStyle(status);
  return (
    <Badge tone={style.tone} variant="subtle" size={size}>
      <span className="inline-flex items-center gap-1">
        <Icon icon={style.icon} size="xs" />
        {style.short}
      </span>
    </Badge>
  );
}

interface JobColumnOptions {
  /** Extra params carried into the drawer link, so filters survive the drill. */
  drillParams?: Record<string, string | string[]>;
  /** Include the SLA and Azure-health columns (the L2 list wants them). */
  detailed?: boolean;
}

export function jobColumns({
  drillParams = {},
  detailed = false,
}: JobColumnOptions = {}): DataGridColumnDef<BackupJobRow>[] {
  const columns: DataGridColumnDef<BackupJobRow>[] = [
    {
      id: "status",
      header: "Result",
      accessor: (job) => job.status,
      sortable: true,
      cell: (job) => <StatusBadge status={job.status} />,
    },
    {
      id: "name",
      header: "Job",
      accessor: (job) => job.name,
      sortable: true,
      cell: (job) => (
        <div className="flex flex-col">
          <DrillLink
            to={BACKUP_JOBS_PATH}
            params={{ ...drillParams, [JOB_DRAWER_PARAM]: jobKey(job.source, job.name) }}
            className="text-fg font-medium hover:underline"
          >
            {job.name}
          </DrillLink>
          <span className="text-fg-muted text-xs">
            {sourceLabel(job.source)}
            {job.job_type ? ` · ${job.job_type}` : ""}
          </span>
        </div>
      ),
    },
    {
      id: "site",
      header: "Site",
      accessor: (job) => job.site ?? "",
      sortable: true,
      cell: (job) => <span className="text-fg-muted text-sm">{job.site ?? "—"}</span>,
    },
  ];

  if (detailed) {
    columns.push({
      id: "sla",
      header: "SLA",
      accessor: (job) => job.sla ?? "",
      sortable: true,
      cell: (job) =>
        job.sla ? (
          <span className="text-fg-muted text-sm tabular-nums">{job.sla}</span>
        ) : (
          <span className="text-fg-muted text-sm">Untiered</span>
        ),
    });
  }

  columns.push(
    {
      id: "duration",
      header: "Duration",
      accessor: (job) => job.duration_sec ?? -1,
      align: "end",
      sortable: true,
      cell: (job) => (
        <span className="text-fg-muted text-xs tabular-nums">
          {formatDuration(job.duration_sec)}
        </span>
      ),
    },
    {
      id: "last_start",
      header: "Last run",
      accessor: (job) => job.last_start ?? "",
      sortable: true,
      cell: (job) => (
        <span className="text-fg-muted text-xs tabular-nums">
          {formatTimestamp(job.last_start)}
        </span>
      ),
    },
    {
      id: "last_success",
      header: "Last success",
      // Never-succeeded sorts to the bottom rather than pretending to be recent.
      accessor: (job) => job.last_success ?? "",
      sortable: true,
      cell: (job) => (
        <span className="text-fg-muted text-xs">
          {job.last_success ? formatRelativeTime(job.last_success) : "Never"}
        </span>
      ),
    },
  );

  if (detailed) {
    columns.push({
      id: "health",
      header: "Health",
      accessor: (job) => job.health_status ?? "",
      cell: (job) => (
        <span className="text-fg-muted text-xs">
          {job.health_status ?? (job.source === "azure-backup" ? "Unknown" : "—")}
        </span>
      ),
    });
  }

  columns.push({
    id: "annotations",
    header: "Acknowledged",
    accessor: (job) => job.annotations,
    align: "end",
    cell: (job) =>
      job.annotations > 0 ? (
        <Badge tone="neutral" variant="subtle" size="xs">
          {formatCount(job.annotations)}
        </Badge>
      ) : (
        <span className="text-fg-muted text-xs">—</span>
      ),
  });

  return columns;
}
